import React, { useRef, useEffect } from 'react';
import { MetricUId } from '../../Metrics/constants';
import WidgetBodyWrapper from '../WidgetBodyWrapper';
import { getMetricDataFromArray } from '../../Metrics/utils';
import ChrMetric from '../../../generic/PageUI/Compound/Metric/ChrMetric';
import getChartConfig from './utils';
import StackedBarChart from '../../Reports/templates/StackedBarChart';
import i18n from '../../../../utils/ChrI18n';
import * as ObjectExtensions from '../../../../utils/CoreExtensions/ObjectExtensions';
import ChrLink from '../../../generic/Utils/ChrLink';
import ModalAndTooltip from '../../../../interfaces/ModalAndTooltip';
import ChrInfoPopover from '../../../generic/Form/Basic/ChrInfoPopover';

const Body = (props) => {
  const { metrics } = props;
  const meetingsBySurveySentimentRef = useRef();
  const surveyReponsesMetricRef = useRef();
  const meetingsWithPositiveSurveyResponses = getMetricDataFromArray(metrics, MetricUId.MEETINGS_WITH_POSITIVE_SURVEY_RESPONSES);
  const meetingsWithNeutralOrNegativeSurveyResponses = getMetricDataFromArray(metrics, MetricUId.MEETINGS_WITH_NEUTRAL_OR_NEGATIVE_SURVEY_RESPONSES);
  const meetingsWithNoSurveyResponses = getMetricDataFromArray(metrics, MetricUId.MEETINGS_WITH_NO_SURVEY_RESPONSES);
  const dataAvailable = metrics.some(metric => metric.data.value > 0);
  const meetingSurveyResponsesGroupedBySurvey = getMetricDataFromArray(metrics, MetricUId.MEETING_SURVEY_RESPONSES_GROUPED_BY_SURVEY);
  const meetingSurveyResponsesGroupedBySurveyValue = meetingSurveyResponsesGroupedBySurvey.data.value;
  const isSurveyResponseEmpty = ObjectExtensions.isEmpty(meetingSurveyResponsesGroupedBySurveyValue);
  const emptySurveyResponseContainerClass = isSurveyResponseEmpty ? 'align-items-center justify-content-center flex-fill' : '';
  const getOptionsForMarker = colorName => ({ show: dataAvailable, className: `cui-management-report-temp-chart-${colorName}` });
  const handleUpdatedManagementReportPositiveOutcomes = (event) => {
    // DashboardReportSubSection::Tile::MEETINGS is referred here
    if(event.detail?.tile === "Meetings") {
      ModalAndTooltip.closeModal();
      props.widgetUtils?.onSubmitHandler();
    }
  };
  const updatedManagementReportPositiveOutcomesEvent = 'updatedManagementReportPositiveOutcomes';

  useEffect(() => {
    const adjustHeight = () => {
      if (meetingsBySurveySentimentRef.current && surveyReponsesMetricRef.current) {
        // The height of survey responses container is set to height of survey sentiment graph container; Since they are in a flex, whenever responses container stretches, graph container also stretches. It poses a challenge to get the actual height of graph container. Below strategy helps.
        surveyReponsesMetricRef.current.style.height = `1px`;
        surveyReponsesMetricRef.current.style.height = `${meetingsBySurveySentimentRef.current.offsetHeight}px`;
      }
    };

    // TODO_AD_FLASH : Add debounce later
    let resizeObserver = new ResizeObserver(adjustHeight);
    resizeObserver.observe(meetingsBySurveySentimentRef.current);
    adjustHeight();

    [updatedManagementReportPositiveOutcomesEvent].forEach(event => document.addEventListener(event, handleUpdatedManagementReportPositiveOutcomes, false));
    return () => {
      [updatedManagementReportPositiveOutcomesEvent].forEach(event => document.removeEventListener(event, handleUpdatedManagementReportPositiveOutcomes, false));
    };
  }, []);

  const positiveOutcomesConfig = meetingsWithPositiveSurveyResponses.positiveOutcomesConfig;

  return (
    <WidgetBodyWrapper { ...props } additionalClassNames="custom-max-height-550-px">
      <div className='d-flex flex-column p-1' ref={ meetingsBySurveySentimentRef }>
        <div className='d-flex flex-column'>
          <div className='d-flex flex-column'>
            <div className="d-flex align-items-center flex-gap-10">
              <div className='cui-management-report-temp-metric-title'>
                <div className="d-flex">
                  <div>
                    { i18n.t("widgets.meeting_surveys.meeting_health_title", jsConstants.customTerms) }
                  </div>
                  <div>
                    <ChrInfoPopover
                      html
                      delay={ 500 }
                      boundary="body"
                      content={ i18n.t("widgets.meeting_surveys.meeting_health_help_text", jsConstants.customTerms) }
                      placement="auto"
                      container="#cjs-management-report-v2"
                      additionalIconClass="px-1 text-info"
                      trigger="click hover"
                    />
                  </div>
                </div>
              </div>
              <div>
                { positiveOutcomesConfig &&
                  <ChrLink
                    onClick={ () => ModalAndTooltip.showModal(positiveOutcomesConfig.url, {}, { largeModal: true }) }
                    noHref
                    trackClick
                    eventDetails={ positiveOutcomesConfig.eventDetails }
                  >
                    <i className="fa-regular fa-cog"></i>
                  </ChrLink>
                }
              </div>
            </div>
            { dataAvailable &&
              <StackedBarChart chartOptions={getChartConfig({ metrics })} />
            }
          </div>
          <WidgetBodyWrapper { ...props } bodyClassName="d-flex flex-xs-wrap" wrapperPadding="py-3 px-1" >
            <ChrMetric className="flex-1" {...meetingsWithPositiveSurveyResponses} marker={getOptionsForMarker("blue")} />
            <ChrMetric className="flex-1" {...meetingsWithNeutralOrNegativeSurveyResponses} marker={getOptionsForMarker("orange")} />
            <ChrMetric className="flex-1" {...meetingsWithNoSurveyResponses} marker={getOptionsForMarker("gray")} />
          </WidgetBodyWrapper>
        </div>
      </div>
      <div className='d-flex flex-column p-1' ref={ surveyReponsesMetricRef }>
        <div className='cui-management-report-temp-metric-title pb-2'>
          { i18n.t("metrics.connection_survey_responses_grouped_by_survey.survey_with_responses_count", { count: ObjectExtensions.length(meetingSurveyResponsesGroupedBySurveyValue) }) }
        </div>
        <div className={ `d-flex flex-column py-2 overflow-scroll custom-thin-scroll-bar vertical-scrollbox ${emptySurveyResponseContainerClass} cui-management-report-temp-metric-submetric-link` }>
          { !isSurveyResponseEmpty &&
            ObjectExtensions.map(meetingSurveyResponsesGroupedBySurveyValue, ([attr, value]) => {
              return (
                <ChrLink newTab key={ attr } href={ value.url } className="p-2 cui-hover-effect" eventDetails={ meetingSurveyResponsesGroupedBySurvey.eventDetails } trackClick>
                  <div className='whitespace-nowrap truncate-with-ellipsis' dangerouslySetInnerHTML={{ __html: i18n.t("metrics.connection_survey_responses_grouped_by_survey.no_of_responses_in_survey_html", { count: value.responseIds.length, survey_name: value.name }) }}>
                  </div>
                </ChrLink>
              );
            })
          }
          { isSurveyResponseEmpty &&
            <div>
              { i18n.t("display_string.see_n_results.zero") }
            </div>
          }
        </div>
      </div>
    </WidgetBodyWrapper>
  );
};

export default Body;
